import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAcsVisitsIndicatorC5Data } from "@/services/esus/indicator/esf/c5/getDetailedCitizensAcsVisitsIndicatorC5";
import { getCitizenScoreStatusDescriptionIndicatorC5 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC5CitizensAcsVisitsFilters } from "./IndicatorEsfC5CitizensAcsVisitsFilters";
import { IndicatorEsfC5CitizensAcsVisitsTable } from "./IndicatorEsfC5CitizensAcsVisitsTable";
import { IndicatorEsfC5CitizensAcsVisitsHistory } from "./IndicatorEsfC5CitizensAcsVisitsHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC5CitizensAcsVisitsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensAcsVisits: Paginated<DetailedCitizenAcsVisitsIndicatorC5Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensAcsVisitsIndicatorC5: () => Promise<
		DetailedCitizenAcsVisitsIndicatorC5Data[]
	>;
};

export function IndicatorEsfC5CitizensAcsVisits({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensAcsVisits,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensAcsVisitsIndicatorC5: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC5CitizensAcsVisitsProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAcsVisits, setCitizenWithAcsVisits] =
		useState<DetailedCitizenAcsVisitsIndicatorC5Data>(
			new DetailedCitizenAcsVisitsIndicatorC5Data()
		);
	const [isLoadingCitizenWithAcsHomeVisits, setIsLoadingCitizenWithAcsHomeVisits] =
		useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAcsVisits(
		citizenWithHomeVisits: DetailedCitizenAcsVisitsIndicatorC5Data
	) {
		setIsLoadingCitizenWithAcsHomeVisits(true);
		setCitizenWithAcsVisits(citizenWithHomeVisits);
		setIsLoadingCitizenWithAcsHomeVisits(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithAcsVisitsToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Visitas ACS - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithAcsVisitsToGenericObjectArray(
		citizensWithAcsVisits: DetailedCitizenAcsVisitsIndicatorC5Data[]
	) {
		return citizensWithAcsVisits.map(
			(citizenWithAcsVisits) =>
				({
					Nome: citizenWithAcsVisits.fullName,
					DN: dayjs(citizenWithAcsVisits.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithAcsVisits.birthdate),
					Documento: citizenWithAcsVisits.cpf ?? citizenWithAcsVisits.cns ?? "",
					Area: citizenWithAcsVisits.area ?? "FA",
					"Ine Equipe": citizenWithAcsVisits.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithAcsVisits.healthTeam?.name ?? "",
					"1ª Visita": citizenWithAcsVisits.firstAcsHomeVisit?.date
						? dayjs(citizenWithAcsVisits.firstAcsHomeVisit.date).format("DD/MM/YYYY")
						: "",
					"Status 1ª Visita": getCitizenScoreStatusDescriptionIndicatorC5(
						citizenWithAcsVisits.firstAcsHomeVisitStatus
					),
					"Última Visita": citizenWithAcsVisits.lastAcsHomeVisit?.date
						? dayjs(citizenWithAcsVisits.lastAcsHomeVisit.date).format("DD/MM/YYYY")
						: "",
					"Status Última Visita": getCitizenScoreStatusDescriptionIndicatorC5(
						citizenWithAcsVisits.twoHomeVisitsStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAcsHomeVisits || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC5CitizensAcsVisitsFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC5CitizensAcsVisitsTable
							citizensAcsVisits={paginatedCitizensAcsVisits.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAcsVisits}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensAcsVisits?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensAcsVisits.pageSize}
								totalPages={paginatedCitizensAcsVisits.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Visitas dos ACS
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC5CitizensAcsVisitsHistory
							citizenWithAcsHomeVisits={citizenWithAcsVisits}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
